<template>
  <div class="request-logs-table">
    <hs-table-shell
      title="Request logs"
      description="Inspect API traffic, latency, status codes, and request context."
    >
      <template #filters>
        <div class="request-logs-table__filters">
          <hs-input
            v-model="localFilters.endpoint"
            label="Endpoint"
            icon="search"
            placeholder="Search endpoint..."
            @update:modelValue="debouncedFilterChange"
          />
          <hs-select
            v-model="localFilters.method"
            label="Method"
            icon="filter"
            :options="methodOptions"
            @change="handleFilterChange"
          />
          <hs-input
            v-model="localFilters.status_code"
            type="number"
            label="Status Code"
            icon="hash"
            placeholder="e.g. 200"
            @update:modelValue="debouncedFilterChange"
          />
          <hs-input
            v-model="localFilters.duration_min"
            type="number"
            label="Min Duration (ms)"
            icon="clock"
            placeholder="e.g. 1000"
            @update:modelValue="debouncedFilterChange"
          />
          <hs-input
            v-model="localFilters.duration_max"
            type="number"
            label="Max Duration (ms)"
            icon="clock"
            placeholder="e.g. 5000"
            @update:modelValue="debouncedFilterChange"
          />
          <hs-input
            v-model="localFilters.user_id"
            label="User ID"
            icon="user"
            placeholder="User ID..."
            @update:modelValue="debouncedFilterChange"
          />
          <hs-input
            v-model="localFilters.store_id"
            label="Store ID"
            icon="shopping-bag"
            placeholder="Store ID..."
            @update:modelValue="debouncedFilterChange"
          />
          <div class="request-logs-table__filter-actions">
            <hs-button
              variant="secondary"
              icon="x-circle"
              block
              @click="clearFilters"
            >
              Clear
            </hs-button>
            <hs-button
              icon="refresh-cw"
              block
              @click="$emit('refresh')"
            >
              Refresh
            </hs-button>
          </div>
        </div>
      </template>

      <div v-if="loading" class="request-logs-table__loading">
        <hs-table-skeleton :columns="9" :rows="pagination?.itemsPerPage || 10" />
      </div>

      <hs-table
        v-else
        :headers="headers"
        :data="logs"
        :header-key-mapping="headerKeyMapping"
      >
        <template #header-endpoint>
          <button class="request-logs-table__sortable" @click="handleSort('endpoint')">
            Endpoint
            <span v-if="sortField === 'endpoint'">{{ sortIndicator }}</span>
          </button>
        </template>

        <template #header-method>
          <button class="request-logs-table__sortable" @click="handleSort('method')">
            Method
            <span v-if="sortField === 'method'">{{ sortIndicator }}</span>
          </button>
        </template>

        <template #header-duration>
          <button class="request-logs-table__sortable" @click="handleSort('duration_ms')">
            Duration
            <span v-if="sortField === 'duration_ms'">{{ sortIndicator }}</span>
          </button>
        </template>

        <template #header-status>
          <button class="request-logs-table__sortable" @click="handleSort('status_code')">
            Status
            <span v-if="sortField === 'status_code'">{{ sortIndicator }}</span>
          </button>
        </template>

        <template #header-user_id>
          <button class="request-logs-table__sortable" @click="handleSort('user_id')">
            User ID
            <span v-if="sortField === 'user_id'">{{ sortIndicator }}</span>
          </button>
        </template>

        <template #header-store_id>
          <button class="request-logs-table__sortable" @click="handleSort('store_id')">
            Store ID
            <span v-if="sortField === 'store_id'">{{ sortIndicator }}</span>
          </button>
        </template>

        <template #header-ip_address>
          <button class="request-logs-table__sortable" @click="handleSort('ip_address')">
            IP Address
            <span v-if="sortField === 'ip_address'">{{ sortIndicator }}</span>
          </button>
        </template>

        <template #header-created_at>
          <button class="request-logs-table__sortable" @click="handleSort('created_at')">
            Created At
            <span v-if="sortField === 'created_at'">{{ sortIndicator }}</span>
          </button>
        </template>

        <template #cell-endpoint="{ row }">
          <div class="request-logs-table__endpoint" :title="row.endpoint">
            {{ row.endpoint || "N/A" }}
          </div>
        </template>

        <template #cell-method="{ row }">
          <hs-badge :tone="getMethodTone(row.method)" size="sm">
            {{ row.method || "N/A" }}
          </hs-badge>
        </template>

        <template #cell-duration_ms="{ row }">
          <span class="request-logs-table__mono">
            {{ formatDuration(row.duration_ms) }}
          </span>
        </template>

        <template #cell-status_code="{ row }">
          <hs-badge :tone="getStatusTone(row.status_code)" size="sm">
            {{ row.status_code || "N/A" }}
          </hs-badge>
        </template>

        <template #cell-user_id="{ row }">
          {{ row.user_id || "Anonymous" }}
        </template>

        <template #cell-store_id="{ row }">
          {{ row.store_id || "N/A" }}
        </template>

        <template #cell-ip_address="{ row }">
          <span class="request-logs-table__mono">
            {{ row.ip_address || "-" }}
          </span>
        </template>

        <template #cell-created_at="{ row }">
          {{ formatDateTime(row.created_at) }}
        </template>

        <template #cell-actions="{ row }">
          <hs-button
            variant="ghost"
            size="sm"
            icon="eye"
            @click="$emit('viewDetails', row)"
          >
            Details
          </hs-button>
        </template>
      </hs-table>

      <template #footer>
        <hs-pagination
          :current-page="pagination?.currentPage || 1"
          :total-pages="pagination?.totalPages || 1"
          :total-items="pagination?.totalItems || 0"
          :items-per-page="pagination?.itemsPerPage || 20"
          @previous-page="$emit('previousPage')"
          @next-page="$emit('nextPage')"
          @update:itemsPerPage="$emit('itemsPerPageChange', $event)"
        />
      </template>
    </hs-table-shell>
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from "vue";
import { debounce } from "lodash";
import HsTable from "@/components/table/HSTable.vue";
import HsTableSkeleton from "@/components/table/HSTableSkeleton.vue";
import {
  HsBadge,
  HsButton,
  HsInput,
  HsPagination,
  HsSelect,
  HsTableShell,
} from "@/components/ui";

export default {
  name: "RequestLogsTable",
  components: {
    HsBadge,
    HsButton,
    HsInput,
    HsPagination,
    HsSelect,
    HsTable,
    HsTableShell,
    HsTableSkeleton,
  },
  props: {
    logs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    pagination: {
      type: Object,
      default: null,
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: [
    "filterChange",
    "sortChange",
    "previousPage",
    "nextPage",
    "itemsPerPageChange",
    "viewDetails",
    "refresh",
  ],
  data() {
    return {
      headers: [
        "Endpoint",
        "Method",
        "Duration",
        "Status",
        "User ID",
        "Store ID",
        "IP Address",
        "Created At",
        "Actions",
      ],
      headerKeyMapping: {
        Endpoint: "endpoint",
        Method: "method",
        Duration: "duration_ms",
        Status: "status_code",
        "User ID": "user_id",
        "Store ID": "store_id",
        "IP Address": "ip_address",
        "Created At": "created_at",
        Actions: "actions",
      },
      methodOptions: [
        { value: "", label: "All Methods" },
        { value: "GET", label: "GET" },
        { value: "POST", label: "POST" },
        { value: "PUT", label: "PUT" },
        { value: "PATCH", label: "PATCH" },
        { value: "DELETE", label: "DELETE" },
      ],
    };
  },
  setup(props, { emit }) {
    const sortField = ref("created_at");
    const sortDirection = ref("desc");

    const localFilters = reactive({
      endpoint: "",
      method: "",
      status_code: "",
      duration_min: "",
      duration_max: "",
      user_id: "",
      store_id: "",
    });

    watch(
      () => props.filters,
      (newFilters) => {
        Object.assign(localFilters, {
          endpoint: "",
          method: "",
          status_code: "",
          duration_min: "",
          duration_max: "",
          user_id: "",
          store_id: "",
        });

        Object.entries(newFilters || {}).forEach(([key, value]) => {
          if (key === "endpoint[like]") {
            localFilters.endpoint = String(value).replace(/^%|%$/g, "");
          } else if (key === "method[eq]") {
            localFilters.method = value;
          } else if (key === "status_code[eq]") {
            localFilters.status_code = value;
          } else if (key === "duration_ms[gte]") {
            localFilters.duration_min = value;
          } else if (key === "duration_ms[lte]") {
            localFilters.duration_max = value;
          } else if (key === "user_id[eq]") {
            localFilters.user_id = value;
          } else if (key === "store_id[eq]") {
            localFilters.store_id = value;
          }
        });

        if (newFilters?.sort) {
          const sortValue = newFilters.sort;
          if (sortValue.startsWith("-")) {
            sortField.value = sortValue.substring(1);
            sortDirection.value = "asc";
          } else if (sortValue.startsWith("+")) {
            sortField.value = sortValue.substring(1);
            sortDirection.value = "desc";
          } else {
            sortField.value = sortValue;
            sortDirection.value = "asc";
          }
        }
      },
      { immediate: true, deep: true }
    );

    const handleFilterChange = () => {
      const filters = { ...localFilters };
      const magicFilters = {};

      if (filters.endpoint) {
        magicFilters["endpoint[like]"] = `%${filters.endpoint}%`;
      }
      if (filters.method) {
        magicFilters["method[eq]"] = filters.method;
      }
      if (filters.status_code) {
        magicFilters["status_code[eq]"] = filters.status_code;
      }
      if (filters.duration_min) {
        magicFilters["duration_ms[gte]"] = filters.duration_min;
      }
      if (filters.duration_max) {
        magicFilters["duration_ms[lte]"] = filters.duration_max;
      }
      if (filters.user_id) {
        magicFilters["user_id[eq]"] = filters.user_id;
      }
      if (filters.store_id) {
        magicFilters["store_id[eq]"] = filters.store_id;
      }

      emit("filterChange", magicFilters);
    };

    const debouncedFilterChange = debounce(handleFilterChange, 500);

    const handleSort = (field) => {
      if (sortField.value === field) {
        sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
      } else {
        sortField.value = field;
        sortDirection.value = field === "created_at" ? "desc" : "asc";
      }

      const sortValue = `${sortDirection.value === "desc" ? "" : "-"}${field}`;
      emit("sortChange", sortValue);
    };

    const clearFilters = () => {
      Object.keys(localFilters).forEach((key) => {
        localFilters[key] = "";
      });
      handleFilterChange();
    };

    const getMethodTone = (method) => {
      switch (method) {
        case "GET":
          return "info";
        case "POST":
          return "success";
        case "PUT":
          return "warning";
        case "PATCH":
          return "primary";
        case "DELETE":
          return "danger";
        default:
          return "neutral";
      }
    };

    const getStatusTone = (statusCode) => {
      if (statusCode >= 200 && statusCode < 300) return "success";
      if (statusCode >= 300 && statusCode < 400) return "info";
      if (statusCode >= 400 && statusCode < 500) return "warning";
      if (statusCode >= 500) return "danger";
      return "neutral";
    };

    const formatDuration = (duration) => {
      if (duration === null || duration === undefined) return "N/A";
      return `${duration}ms`;
    };

    const formatDateTime = (isoString) => {
      if (!isoString) return "N/A";
      return new Date(isoString).toLocaleString();
    };

    const sortIndicator = computed(() => (sortDirection.value === "asc" ? "↑" : "↓"));

    return {
      localFilters,
      sortField,
      sortDirection,
      sortIndicator,
      debouncedFilterChange,
      handleFilterChange,
      handleSort,
      clearFilters,
      getMethodTone,
      getStatusTone,
      formatDuration,
      formatDateTime,
    };
  },
};
</script>

<style scoped>
.request-logs-table {
  min-width: 0;
  width: 100%;
}

.request-logs-table__filters {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.request-logs-table__filter-actions {
  align-items: end;
  display: grid;
  gap: 0.55rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.request-logs-table__loading {
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  padding: 0.85rem;
}

.request-logs-table__loading :deep(.hs-table-skeleton-wrap),
.request-logs-table__loading :deep(.hs-table-skeleton) {
  box-shadow: none;
  min-width: max(760px, 100%);
}

.request-logs-table__sortable {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  gap: 0.35rem;
  padding: 0;
  transition: color 0.2s ease;
  white-space: nowrap;
}

.request-logs-table__sortable:hover {
  color: var(--hs-primary, #3293ec);
}

.request-logs-table__endpoint {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 0.76rem;
  max-width: 20rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.request-logs-table__mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-size: 0.8rem;
}

@media (max-width: 1180px) {
  .request-logs-table__filters {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 680px) {
  .request-logs-table__filters,
  .request-logs-table__filter-actions {
    grid-template-columns: 1fr;
  }
}
</style>
